import { isPackhouseProducts, isPackhouseProductsMixed } from 'constants/graphql-typenames';
import { Draft, produce } from 'immer';
import { useCallback } from 'react';
import useStitchedResult from 'hooks/useStitchedResult';
import { DEFAULT_PAGE_NUMBER } from 'utils/constants';
import {
  PackhouseColumn,
  ProductWeightSectionData,
  PackhouseProduct,
  RowNew,
  PackhouseScreen,
  PackhouseScheduleTable,
  ProductTypeSection,
  ProductWeightSection,
  ProductWeightSectionDataType,
  Row,
  PackhouseProductMixed,
} from 'generated/graphql';

const stitchProductAllocations = (allocations: Row[], allocationsNew: Row[]): Row[] => {
  const indexesToSplice: Number[] = [];

  const stitchedProductAllocations = allocations.map((allocation) => {
    const index = allocationsNew.findIndex((curr) => curr.rowID === allocation.rowID);
    if (index === -1) {
      return { ...allocation };
    }

    const allocationNew = allocationsNew[index];
    indexesToSplice.push(index);
    return {
      ...allocationNew,
    };
  });

  const dataToAdd = allocationsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedProductAllocations || []), ...(dataToAdd || [])];
};

const refetchProductIDSet = (refetchProductIDs?: string[]) => {
  if (!refetchProductIDs?.length) {
    return undefined;
  }
  return new Set(refetchProductIDs.map((id) => Number(id)));
};

const mixedProductWasRefetched = (product: PackhouseProductMixed, refetchIDs?: Set<number>) => {
  if (!refetchIDs?.size) {
    return false;
  }
  return product.productIDs.some((id) => refetchIDs.has(Number(id)));
};

const stitchMixedProducts = (
  products: PackhouseProductMixed[],
  productsNew: PackhouseProductMixed[],
  refetchProductIDs?: string[],
): PackhouseProductMixed[] => {
  const refetchIDs = refetchProductIDSet(refetchProductIDs);
  const indexesToSplice: Number[] = [];

  const stitchedProducts = products.flatMap((product) => {
    const index = productsNew.findIndex((curr) => curr.groupID === product.groupID);
    if (index === -1) {
      if (mixedProductWasRefetched(product, refetchIDs)) {
        return [];
      }
      return [product];
    }

    const productNew = productsNew[index];
    indexesToSplice.push(index);
    return [
      {
        ...productNew,
        shipmentAllocations: {
          rows: stitchProductAllocations(product.shipmentAllocations.rows, productNew.shipmentAllocations.rows),
        },
      },
    ];
  });

  const dataToAdd = productsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...stitchedProducts, ...dataToAdd];
};

const stitchProducts = (products: PackhouseProduct[], productsNew: PackhouseProduct[]): PackhouseProduct[] => {
  const indexesToSplice: Number[] = [];

  const stitchedProducts = products.map((product) => {
    const index = productsNew.findIndex((curr) => curr.productID === product.productID);
    if (index === -1) {
      return { ...product };
    }

    const productNew = productsNew[index];
    indexesToSplice.push(index);
    return {
      ...productNew,
      shipmentAllocations: {
        rows: stitchProductAllocations(product.shipmentAllocations.rows, productNew.shipmentAllocations.rows),
      },
    };
  });

  const dataToAdd = productsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedProducts || []), ...(dataToAdd || [])];
};

const stitchProductWeightData = (
  data: ProductWeightSectionData,
  dataNew: ProductWeightSectionData,
  dataType: ProductWeightSectionDataType,
  refetchProductIDs?: string[],
): ProductWeightSectionData => {
  const isProducts =
    dataType === ProductWeightSectionDataType.PackhouseProduct ||
    (isPackhouseProducts(data) && isPackhouseProducts(dataNew));
  const isMixed =
    dataType === ProductWeightSectionDataType.PackhouseProductMixed ||
    (isPackhouseProductsMixed(data) && isPackhouseProductsMixed(dataNew));

  if (isProducts) {
    return {
      products: stitchProducts(data.products as PackhouseProduct[], dataNew.products as PackhouseProduct[]),
    };
  }

  if (isMixed) {
    return {
      products: stitchMixedProducts(
        data.products as PackhouseProductMixed[],
        dataNew.products as PackhouseProductMixed[],
        refetchProductIDs,
      ),
    };
  }

  // For ProductsData or mismatched types, just take the latest
  return dataNew;
};

const stitchProductWeights = (
  productWeights: ProductWeightSection[],
  productWeightsNew: ProductWeightSection[],
  refetchProductIDs?: string[],
): ProductWeightSection[] => {
  const indexesToSplice: Number[] = [];

  const stitchedProductWeights = productWeights.map((productWeight) => {
    const index = productWeightsNew.findIndex((curr) => curr.name === productWeight.name);
    if (index === -1) {
      return { ...productWeight };
    }

    indexesToSplice.push(index);

    return {
      ...productWeight,
      info: productWeightsNew[index].info,
      data: stitchProductWeightData(
        productWeight.data,
        productWeightsNew[index].data,
        productWeight.dataType,
        refetchProductIDs,
      ),
    };
  });

  const dataToAdd = productWeightsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedProductWeights || []), ...(dataToAdd || [])];
};

const stitchProductButtons = <T extends { value: string }>(buttons: T[], buttonsNew: T[]): T[] => {
  const indexesToSplice: Number[] = [];

  const stitchedButtons = buttons.map((button) => {
    const index = buttonsNew.findIndex((curr) => curr.value === button.value);
    if (index === -1) {
      return { ...button };
    }

    indexesToSplice.push(index);
    return {
      ...buttonsNew[index],
    };
  });

  const dataToAdd = buttonsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedButtons || []), ...(dataToAdd || [])];
};

const stitchProductTypes = (
  sections: ProductTypeSection[],
  sectionsNew: ProductTypeSection[],
  refetchProductIDs?: string[],
): ProductTypeSection[] => {
  const indexesToSplice: Number[] = [];

  const stitchedSections = sections.map((section) => {
    const index = sectionsNew.findIndex((curr) => curr.productTypeID === section.productTypeID);
    if (index === -1) {
      return { ...section };
    }

    const sectionNew = sectionsNew[index];
    indexesToSplice.push(index);

    return {
      ...section,
      buttons: stitchProductButtons(section.buttons, sectionNew.buttons),
      productTypeEnabled: sectionNew.productTypeEnabled || section.productTypeEnabled,
      productWeights: stitchProductWeights(section.productWeights, sectionNew.productWeights, refetchProductIDs),
    };
  });

  const dataToAdd = sectionsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedSections || []), ...(dataToAdd || [])];
};

const stitchShipments = (columns: PackhouseColumn[], columnsNew: PackhouseColumn[]) => {
  if (!columns || !columnsNew) {
    return columnsNew;
  }

  const indexesToSplice: Number[] = [];

  const data = columns.map((column) => {
    const index = columnsNew.findIndex((curr) => curr.ID === column.ID);

    if (index === -1) {
      return { ...column };
    }

    indexesToSplice.push(index);
    return {
      ...columnsNew[index],
    };
  });

  const dataToAdd = columnsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(data || []), ...(dataToAdd || [])];
};

const stitchTable = (
  packhouseTable: PackhouseScheduleTable,
  packhouseTableNew: PackhouseScheduleTable,
  refetchProductIDs?: string[],
): PackhouseScheduleTable => {
  if (!packhouseTable || !packhouseTableNew) {
    return packhouseTableNew;
  }

  return {
    ...packhouseTable,
    shipments: stitchShipments(packhouseTable.shipments, packhouseTableNew.shipments),
    products: stitchProductTypes(packhouseTable.products, packhouseTableNew.products, refetchProductIDs),
    packhouseAllocation: packhouseTableNew.packhouseAllocation,
    pageInfos: packhouseTableNew.pageInfos,
  };
};

const stitchSchedule = (
  schedule: PackhouseScreen | undefined,
  scheduleNew: PackhouseScreen,
  refetchProductIDs?: string[],
) => {
  if (!schedule || !scheduleNew) {
    return scheduleNew;
  }

  return {
    ...scheduleNew,
    allocations: stitchTable(schedule.allocations, scheduleNew.allocations, refetchProductIDs),
  };
};

export const useStitchPackhouse = (
  data: PackhouseScreen | undefined,
  allowReset: boolean,
  refetchProductIDs?: string[],
) => {
  const [schedule, setSchedule] = useStitchedResult(data, (previous, next) =>
    next?.allocations.pageInfos.vertical.page === DEFAULT_PAGE_NUMBER && allowReset
      ? next
      : stitchSchedule(previous, next, refetchProductIDs),
  );

  const set = useCallback(
    (updater: (draft: Draft<PackhouseScreen | undefined>) => void) => {
      setSchedule(produce(updater));
    },
    [setSchedule],
  );

  return {
    packhouseSchedule: schedule,
    set,
    clear: () => setSchedule(undefined),
    stitchSchedule,
  };
};
